import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PageHeader from '@/components/PageHeader'
import { Card, Row, Col, List, Tag, Pagination, Button, message, Badge } from 'antd'
import { SelectC } from '@/i2antdV5'
import ApiPollingManager from '@/utils/ApiPollingManager'
import EzvizPlayer from '@/components/EzvizPlayer'

import { fetchAlerts } from '@/redux/alerts/alertsSlice'
import {
  fetchMonitoringGroupUnprocessed,
  unregisterMonitoringGroupUnprocessedReconnect
} from '@/redux/alerts/monitoringGroupUnprocessedSlice'
import { fetchStoreList } from '@/redux/dashboard/storeList/storeSlice'
import { fetchDeviceList } from '@/redux/ys7/deviceSlice'
// import { fetchAlarmList } from '@/redux/ys7/alarmSlice'

import NursingAlertsDrawer from '@/views/commandCenter/share/NursingAlertsDrawer'
import { renderTextStr, formatDateTimeStr } from '@/utils/renderUtils'
import { ALERT_LEVEL_CONFIG, NURSING_ALERT_STATUS_CONFIG } from '@/views/commandCenter/config'

import ServiceAlertsModal from '@/views/commandCenter/share/ServiceAlertsModal'
import CardServiceAlertsDrawer from '@/views/commandCenter/share/CardServiceAlertsDrawer'
import MonitoringSseAlertsPanel from '@/views/commandCenter/profile/ServiceAlerts/MonitoringSseAlertsPanel'
import {
  getAlarmListFromMonitoringGroup,
  getMessageBody,
  getSeeData
} from '@/views/commandCenter/profile/ServiceAlerts/monitoringSsePayloadUtils'

import {
  connectSse,
  disconnectSse,
  SSE_CONSUMER_IDS,
  getMonitoringSseMessageList,
  getMonitoringAlarmSseList,
  getMonitoringAlarmProcessedStreamKeysMap
} from '@/redux/sse/sseSlice'

import './ServiceAlertsList.less'

// 从当前网页地址解析默认查询参数（如 ?store_id=xx）
const getDefaultQuery = () => {
  let storeId = '';

  try {
    const { search } = window.location || {};
    if (search) {
      const params = new URLSearchParams(search);
      const storeIdParam = params.get('store_id') || params.get('storeId');

      if (storeIdParam) {
        // 转换为字符串，与 options 中的 id 类型保持一致（options.id 是字符串）
        const parsed = parseInt(storeIdParam, 10)
        if (!Number.isNaN(parsed)) {
          storeId = String(parsed)
        }
      }
    }
  } catch (e) {
    // 容错：在非浏览器环境或解析异常时，回退到默认值
  }

  return {
    store_id: storeId
  }
}
const breadcrumbList = [
  { breadcrumbName: 'dashboard', href: '/client/commandCenter' },
  { breadcrumbName: '服务预警详情' }
]

/**
 * Col 布局配置管理
 * 统一管理页面中所有 Col 组件的响应式配置
 */
const COL_LAYOUT_CONFIG = {
  // 预警卡片布局
  alertCard: {
    xs: 24,
    sm: 24,
    md: 12,
    lg: 8,
    xl: 8
  },
  // 监控预警：左侧一列
  monitoringCard: {
    xs: 24,
    sm: 24,
    md: 24,
    lg: 8,
    xl: 8
  },
  // 右侧护理/产康：各占半行（12/24）
  rightAlertCard: {
    xs: 24,
    sm: 24,
    md: 12,
    lg: 12,
    xl: 12
  }
}

const normalizeStoreId = (raw) => {
  if (raw == null || raw === '') return ''
  return String(raw).trim()
}

const getSelectedStoreId = (query = {}) => normalizeStoreId(query.storeId ?? query.store_id)

const getMonitoringGroupParams = (query = {}) => {
  const storeId = getSelectedStoreId(query)
  return storeId ? { force: true, storeId } : { force: true }
}

const getMonitoringSseStoreId = (item) => {
  const seeData = getSeeData(item)
  const body = getMessageBody(seeData)
  const outer = item?.data
  return normalizeStoreId(
    seeData.storeId ??
    seeData.store_id ??
    body?.storeId ??
    body?.store_id ??
    outer?.storeId ??
    outer?.store_id
  )
}

const getMonitoringAlarmStoreId = (alarm) => {
  if (!alarm || typeof alarm !== 'object') return ''
  const m = alarm.messageMap || alarm.messageBody
  return normalizeStoreId(alarm.storeId ?? alarm.store_id ?? m?.storeId ?? m?.store_id)
}

const getMonitoringGroupStoreId = (group) => {
  if (!group || typeof group !== 'object') return ''
  return normalizeStoreId(group.storeId ?? group.store_id)
}

const filterMonitoringGroupByStoreId = (group, selectedStoreId) => {
  if (!selectedStoreId) return group
  const groupStoreId = getMonitoringGroupStoreId(group)
  if (groupStoreId) return groupStoreId === selectedStoreId ? group : null

  const alarms = getAlarmListFromMonitoringGroup(group)
  if (alarms.length === 0) return null
  const filteredAlarms = alarms.filter((alarm) => getMonitoringAlarmStoreId(alarm) === selectedStoreId)
  if (filteredAlarms.length === 0) return null
  return {
    ...group,
    alarmList: filteredAlarms
  }
}

const filterMonitoringGroupUnprocessedByStoreId = (monitoringGroupUnprocessed, selectedStoreId) => {
  if (!selectedStoreId) return monitoringGroupUnprocessed
  const groups = Array.isArray(monitoringGroupUnprocessed?.groups)
    ? monitoringGroupUnprocessed.groups
    : []
  return {
    ...monitoringGroupUnprocessed,
    groups: groups
      .map((group) => filterMonitoringGroupByStoreId(group, selectedStoreId))
      .filter(Boolean)
  }
}

const filterMonitoringSseMessagesByStoreId = (messages, selectedStoreId) => {
  if (!selectedStoreId) return messages
  if (!Array.isArray(messages)) return []
  return messages.filter((item) => getMonitoringSseStoreId(item) === selectedStoreId)
}

/**
 * 接口配置管理
 * 类型说明：
 * - 'once': 只需加载一次，组件挂载时执行
 * - 'realtime': 实时获取，页面可见时持续轮询
 * - 'polling': 定时获取，按设定间隔轮询
 * - 'manual': 手动触发，不自动获取
 */
const getAPIConfig = (componentInstance) => ({
  // 只需加载一次的接口配置（组件挂载时执行）
  once: [
    {
      name: 'fetchStoreList',
      dispatch: fetchStoreList,
      getParams: () => ({
        serviceType: 'default',
        dataType: 'storeOption'
      })
    },
    {
      name: 'fetchDeviceList',
      dispatch: fetchDeviceList,
      getParams: () => {
        // api.js 会自动从 Redux 获取 accessToken 并添加到请求中
        // 不需要手动传入 accessToken
        return {
          pageStart: 0,
          pageSize: 100 // 获取前100个设备
        }
      }
    },
  ],

  // 实时获取接口配置（页面可见时持续轮询）
  realtime: [],

  // 定时获取接口配置
  polling: [
    {
      name: 'fetchAlerts',
      dispatch: fetchAlerts,
      interval: 30000, // 30秒间隔（占位符，可自行设置）
      getParams: (query) => ({
        serviceType: 'nursing',
        query: { ...query }
      })
    },
    // {
    //   name: 'fetchAlarmList',
    //   dispatch: fetchAlarmList,
    //   interval: 30000, // 30秒间隔，与 fetchAlerts 同步
    //   getParams: () => {
    //     // api.js 会自动从 Redux 获取 accessToken 并添加到请求中
    //     const now = Date.now()
    //     const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000
    //     return {
    //       startTime: sevenDaysAgo,
    //       endTime: now,
    //       status: 0, // 默认查未读
    //       pageStart: 0,
    //       pageSize: 100 // 获取前100条告警
    //     }
    //   }
    // }
  ],

  // 手动触发接口（不自动获取，但 initData 会调用 fetchData 执行所有 manual 接口）
  manual: [
    {
      /**
       * 监控预警：按设备序列号+通道分组的未处理列表。
       * 跟随页面店铺筛选刷新，Java 接口参数使用 storeId。
       */
      name: 'fetchMonitoringGroupUnprocessed',
      dispatch: fetchMonitoringGroupUnprocessed,
      getParams: getMonitoringGroupParams
    }
  ]
})

class ServiceAlertsList extends React.Component {
  constructor(props) {
    super(props)
    // 创建 API 轮询管理器（传入组件实例以访问 Redux state）
    this.apiManager = new ApiPollingManager(getAPIConfig(this), this)
    // 存储计算的高度值
    this.state = {
      alertCardHeight: null,
      monitoringAlertCardHeight: null, // 与右侧预警卡片同行等高
      videoCardHeight: 200,
      isCompressed: false, // 标记内容是否被压缩
      /** 视频监控 clickToPlay：各容器 resetStreamSignal 计数，用于停止取流 */
      monitoringStreamReset: {},
      /** 各监控格是否正在取流（用于显示「停止」按钮） */
      monitoringPlayingByContainer: {}
    }
    // 存储视频播放器实例
    this.videoPlayers = {}
    // SSE 调试打印节流
    this._lastSseLogAt = 0
    this._lastSseTotalReceived = 0
  }

  /**
   * 页面卸载前关闭 SSE 连接
   */
  handleBeforeUnload = () => {
    const { dispatch } = this.props
    if (dispatch) {
      dispatch(disconnectSse({ consumerId: SSE_CONSUMER_IDS.SERVICE_ALERTS_LIST }))
    }
  }

  /**
   * 清理所有视频播放器（子组件卸载时也会各自销毁；此处兜底全停）
   */
  cleanupVideoPlayers = () => {
    Object.keys(this.videoPlayers).forEach(containerId => {
      try {
        const player = this.videoPlayers[containerId]
        if (player) {
          player.stop?.()
          player.destroy?.()
        }
      } catch (e) {
        console.warn('清理播放器失败:', e)
      }
    })
    this.videoPlayers = {}
  }

  /**
   * 视频监控格：停止取流（与 EzvizPlayer resetStreamSignal 配合）
   */
  stopMonitoringStream = (containerId) => {
    this.setState((prev) => ({
      monitoringStreamReset: {
        ...prev.monitoringStreamReset,
        [containerId]: (prev.monitoringStreamReset[containerId] || 0) + 1
      }
    }))
  }

  /**
   * 计算并设置各模块高度，确保在一屏内显示
   */
  calculateHeights = () => {
    // 获取视口高度和宽度
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    // 头部高度（PageHeader）
    const headerHeight = 127
    // 工具栏高度（i2-app-toolbar）- 需要实际测量
    const toolbarHeight = 50
    // Card body 的 padding（i2-card-page 的 padding）
    const cardPagePadding = 8 * 2 // 上下各 8px
    // Row 之间的间距
    const rowGap = 6
    // 视频监控模块高度（固定）
    const videoCardHeight = 200
    // Card head 高度（压缩后）
    const cardHeadHeight = 32

    // 计算预警卡片区域的可用高度
    // 总可用高度 = 视口高度 - 头部 - 工具栏 - Card页面padding - 视频模块高度 - 行间距
    const availableHeight = viewportHeight - headerHeight - toolbarHeight - cardPagePadding - videoCardHeight - rowGap * 2
    
    // 预警卡片区域：左侧监控预警与右侧护理/产康同一行等高
    const alertCardHeight = Math.floor(availableHeight)
    const monitoringAlertCardHeight = alertCardHeight
    
    // 确保最小高度
    const minAlertCardHeight = 200
    
    // 检测是否被压缩：如果计算出的高度小于最小高度，说明内容被压缩
    const isCompressed = alertCardHeight < minAlertCardHeight || viewportWidth < 768
    
    const finalAlertCardHeight = isCompressed ? null : Math.max(alertCardHeight, minAlertCardHeight)
    const finalMonitoringAlertCardHeight = isCompressed ? null : Math.max(monitoringAlertCardHeight, minAlertCardHeight)

    this.setState({
      alertCardHeight: finalAlertCardHeight,
      monitoringAlertCardHeight: finalMonitoringAlertCardHeight,
      videoCardHeight: isCompressed ? null : videoCardHeight,
      isCompressed: isCompressed
    })
    this._heightsCalculated = true

    // 设置 CSS 变量供样式使用
    if (!isCompressed) {
      document.documentElement.style.setProperty('--alert-card-height', `${finalAlertCardHeight}px`)
      document.documentElement.style.setProperty('--video-card-height', `${videoCardHeight}px`)
    } else {
      // 解除限制时，移除 CSS 变量
      document.documentElement.style.removeProperty('--alert-card-height')
      document.documentElement.style.removeProperty('--video-card-height')
    }
  }

  componentDidMount() {
    const initialQuery = getDefaultQuery()
    this.apiManager.init(initialQuery)
    
    // 初始化高度计算（延迟执行，确保 DOM 已渲染）
// ====================展示关闭 直播查看===================================
    setTimeout(() => {
      this.calculateHeights()
    }, 300)
// =======================================================

    
    // 监听窗口大小变化，重新计算高度
    window.addEventListener('resize', this.calculateHeights)
    // 监听窗口可见性变化（切换标签页、最小化等）
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    // 监听窗口焦点变化
    window.addEventListener('focus', this.handleWindowFocus)
    // 监听其他窗口的暂停请求（通过 storage 事件）
    window.addEventListener('storage', this.handleStorageEvent)
    // 监听 BroadcastChannel 消息（如果支持）
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this._broadcastChannel = new BroadcastChannel('videoPlayerControl')
        this._broadcastChannel.addEventListener('message', this.handleBroadcastMessage)
      } catch (e) {
        // BroadcastChannel 不支持，忽略
      }
    }
    // 进入预警页：强制重建 SSE（与首页复用的长连脱钩，恢复「打开即重连」行为）
    if (this.props.dispatch) {
      this.props.dispatch(connectSse({
        consumerId: SSE_CONSUMER_IDS.SERVICE_ALERTS_LIST,
        force: true
      }))
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  }

  componentWillUnmount() {
    // 清理管理器
    this.apiManager.cleanup()
    // 移除窗口大小变化监听
    window.removeEventListener('resize', this.calculateHeights)
    // 移除窗口可见性和焦点监听
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('focus', this.handleWindowFocus)
    // 移除 storage 事件监听
    window.removeEventListener('storage', this.handleStorageEvent)
    // 关闭 BroadcastChannel
    if (this._broadcastChannel) {
      try {
        this._broadcastChannel.removeEventListener('message', this.handleBroadcastMessage)
        this._broadcastChannel.close()
        this._broadcastChannel = null
      } catch (e) {
        // 忽略错误
      }
    }
    // 清理所有视频播放器
    this.cleanupVideoPlayers()
    unregisterMonitoringGroupUnprocessedReconnect()
    // 页面卸载时关闭 SSE 连接，并移除 beforeunload 监听
    if (this.props.dispatch) {
      this.props.dispatch(disconnectSse({ consumerId: SSE_CONSUMER_IDS.SERVICE_ALERTS_LIST }))
    }
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  }

  /**
   * 处理 localStorage 事件（来自其他窗口的暂停请求）
   */
  handleStorageEvent = (e) => {
    if (e.key === 'videoPlayerPauseRequest' && e.newValue) {
      // 其他窗口请求暂停播放器
      this.pauseVideoPlayers()
    }
  }

  /**
   * 处理 BroadcastChannel 消息（来自其他窗口的暂停请求）
   */
  handleBroadcastMessage = (e) => {
    if (e.data && e.data.type === 'pauseAll') {
      // 其他窗口请求暂停播放器
      this.pauseVideoPlayers()
    }
  }

  /**
   * 处理窗口可见性变化（切换标签页、最小化等）
   */
  handleVisibilityChange = () => {
    const isVisible = !document.hidden
    if (isVisible) {
      // 窗口变为可见，恢复播放器
      this.resumeVideoPlayers()
    } else {
      // 窗口变为不可见，暂停播放器
      this.pauseVideoPlayers()
    }
  }

  /**
   * 处理窗口获得焦点
   */
  handleWindowFocus = () => {
    // 窗口获得焦点，恢复播放器
    this.resumeVideoPlayers()
  }

  /**
   * 暂停所有视频播放器（简单实现：只停止播放，不销毁）
   */
  pauseVideoPlayers = () => {
    Object.keys(this.videoPlayers).forEach(containerId => {
      try {
        const player = this.videoPlayers[containerId]
        if (player && player.stop && typeof player.stop === 'function') {
          try {
            player.stop()
          } catch (e) {
            console.warn(`暂停播放器 ${containerId} 失败:`, e)
          }
        }
      } catch (e) {
        console.warn(`暂停播放器 ${containerId} 失败:`, e)
      }
    })
  }

  /**
   * 恢复所有视频播放器（窗口重新获得焦点时调用）
   */
  resumeVideoPlayers = () => {
    // 延迟恢复，确保窗口已完全激活
    setTimeout(() => {
      Object.keys(this.videoPlayers).forEach(containerId => {
        try {
          const player = this.videoPlayers[containerId]
          if (player && player.play && typeof player.play === 'function') {
            try {
              player.play().catch(e => {
                console.warn(`恢复播放器 ${containerId} 失败:`, e)
              })
            } catch (e) {
              console.warn(`恢复播放器 ${containerId} 失败:`, e)
            }
          }
        } catch (e) {
          console.warn(`恢复播放器 ${containerId} 失败:`, e)
        }
      })
    }, 200)
  }

  /**
   * 更新查询参数
   */
  updateQuery = (query) => {
    // 更新管理器中的查询参数
    this.apiManager.updateQuery(query)
    // 手动触发数据获取（重置为第一页）
    this.handlePageChange(1, undefined, query)
  }

  /**
   * 分页切换
   */
  handlePageChange = (page, pageSize, query) => {
    // 使用传入的 query，如果为空则使用默认值
    const baseQuery = query && Object.keys(query).length > 0 ? query : getDefaultQuery()
    const finalQuery = {
      ...baseQuery,
      pageNumber: page,
      pageSize: pageSize || baseQuery.pageSize || 20
    }

    this.apiManager.updateQuery(finalQuery)
    this.apiManager.fetchData(finalQuery, true)
  }

  /**
   * 根据预警等级返回统一配置（颜色 / 文案 / 样式等）
   * level 可能是数字，也可能是接口枚举对象 { value, name, desc }
   */
  getAlertConfig = (level) => {
    let levelKey = level
    if (level && typeof level === 'object' && !Array.isArray(level)) {
      levelKey = level.value != null ? level.value : level.name
    }
    const levelNum = parseInt(levelKey, 10)
    const config = ALERT_LEVEL_CONFIG[levelNum] || {}
    // 禁止把对象直接塞进 Tag children（会触发 Objects are not valid as a React child）
    const fallbackText =
      level && typeof level === 'object' && !Array.isArray(level)
        ? renderTextStr(level.desc != null && level.desc !== '' ? level.desc : levelKey)
        : renderTextStr(levelKey)

    return {
      level: Number.isFinite(levelNum) ? levelNum : levelKey,
      text: config.text || fallbackText || '-',
      color: config.color || 'default',
      tagStyle: config.tagStyle || { marginRight: 8, fontWeight: 'bold' },
      borderLeft: config.borderLeft || '4px solid #d9d9d9',
      backgroundColor: config.backgroundColor || 'transparent'
    }
  }

  /**
   * 根据护理预警状态返回统一配置（颜色 / 文案 / 样式等）
   */
  getStatusConfig = (status) => {
    const statusKey =
      status && typeof status === 'object' && !Array.isArray(status)
        ? status.name ?? status.value ?? status.desc ?? status
        : status
    const config = NURSING_ALERT_STATUS_CONFIG[statusKey] || {}

    return {
      text: config.text || renderTextStr(statusKey) || '-',
      color: config.color || 'default',
      tagStyle: config.tagStyle || { marginRight: 8 }
    }
  }

  /**
   * 获取监控设备列表
   * 优先使用萤石云设备列表，如果没有则使用监控预警数据
   */
  getMonitoringDevices = (monitoringList = []) => {
    const { ys7DeviceObj } = this.props
    // deviceSlice 已经保证了 deviceList.data 始终是数组
    const { deviceList: { data: deviceListData } } = ys7DeviceObj
    // 优先使用萤石云设备列表
    if (deviceListData.length > 0) {
      const devices = deviceListData
      
      // 映射萤石云设备数据格式
      // 萤石云设备字段：deviceSerial, deviceName, status (0-离线, 1-在线)
      return devices.slice(0, 4).map((device, index) => ({
        deviceSerial: device.deviceSerial || device.device_serial || `DEVICE_${index}`,
        deviceName: device.deviceName || device.device_name || device.name || `监控设备 ${index + 1}`,
        status: device.status !== undefined ? device.status : (device.onlineStatus !== undefined ? device.onlineStatus : 1), // 0-离线, 1-在线
        channelNo: device.channelNo || device.channel_no || 1
      }))
    }
    
    // 如果没有萤石云设备列表，从监控预警数据中提取设备信息
    const devices = monitoringList.map((alert, index) => ({
      deviceSerial: alert.device_serial || alert.deviceSerial || `DEVICE_${index}`,
      deviceName: alert.device_name || alert.deviceName || alert.customer_name || `监控设备 ${index + 1}`,
      status: alert.device_status ?? 1, // 1-在线，0-离线
      channelNo: alert.channel_no || 1
    }));

    console.log('devices', devices)
    // 如果都没有数据，返回空数组（不返回示例数据）
    return devices;
  }

  /**
   * 查看监控画面详情
   */
  handleViewMonitoringDetail = (device) => {
    // 跳转到监控画面详情页或打开弹窗
    const url = `/client/ServiceAlerts/VideoProfile?deviceSerial=${device.deviceSerial}&channelNo=${device.channelNo}`;
    window.open(url, '_blank');
  }

  /**
   * 合并萤石云告警数据到监控预警列表
   * 将萤石云告警数据格式转换为监控预警格式
   * 
   * 萤石云告警字段映射：
   * - alarmId -> id (消息ID)
   * - alarmName -> alert_type (告警源名称)
   * - alarmType -> (告警类型，可用于判断等级)
   * - alarmTime -> updated_at, create_time (告警时间，毫秒转秒)
   * - deviceSerial -> device_serial (设备序列号)
   * - isChecked -> status (是否已读：0-未读，1-已读)
   * - channelNo -> channel_no (通道号)
   * - alarmPicUrl -> (告警图片地址，保留在原始数据中)
   */
  getMergedMonitoringServiceList = () => {
    const { alertsObj, ys7AlarmObj, ys7DeviceObj, sseObj } = this.props
    // deviceSlice 已经保证了 deviceList.data 始终是数组
    const { deviceList: { data: deviceListData, query: deviceListQuery } } = ys7DeviceObj
    const { monitoringService: { list: monitoringServiceList = [] } = {} } = alertsObj
    
    // 获取萤石云告警数据
    const { alarmList: { data: ys7AlarmList } } = ys7AlarmObj

    // 获取监控相关 SSE：monitoring_alarm 分桶 + 兼容 message
    const sseMessages = getMonitoringSseMessageList(sseObj)
    
    // 创建设备序列号到设备名称的映射
    const deviceMap = {}
    deviceListData.forEach(device => {
      const serial = device.deviceSerial || device.device_serial
      if (serial) {
        deviceMap[serial] = device.deviceName || device.device_name || device.name || serial
      }
    })
    
    // 将萤石云告警数据转换为监控预警格式
    const convertedYs7Alarms = ys7AlarmList.map((alarm, index) => {
      // 告警时间处理：alarmTime 是毫秒级时间戳
      const alarmTimeMs = alarm.alarmTime ?? Date.now()
      const alarmTime = typeof alarmTimeMs === 'number' ? alarmTimeMs : new Date(alarmTimeMs).getTime()
      
      // 根据告警类型判断等级（可根据实际业务需求调整）
      const getAlertLevelByType = (alarmType) => {
        // 告警类型映射：可根据实际业务需求调整
        // 1-移动侦测，2-声音报警，3-视频遮挡，4-视频丢失等
        if (alarmType === 1 || alarmType === 2) return 2 // 中等
        if (alarmType === 3 || alarmType === 4) return 3 // 较高
        return 2 // 默认中等
      }
      
      const deviceSerial = alarm.deviceSerial || ''
      const deviceName = deviceMap[deviceSerial] || deviceSerial || '未知设备'
      
      return {
        // 监控预警格式字段
        id: alarm.alarmId || `ys7_alarm_${index}`,
        alert_level: getAlertLevelByType(alarm.alarmType),
        alert_type: alarm.alarmName || `告警类型${alarm.alarmType || ''}`,
        customer_name: deviceName,
        device_name: deviceName,
        device_serial: deviceSerial,
        channel_no: alarm.channelNo || 1,
        status: alarm.isChecked ?? 0, // isChecked: 0-未读，1-已读
        create_time: Math.floor(alarmTime / 1000), // 转换为秒级时间戳
        updated_at: Math.floor(alarmTime / 1000),
        // 标记为萤石云告警
        _ys7Alarm: true,
        // 直接使用原始告警数据，保留所有原始字段
        ...alarm
      }
    })
    
    // 合并监控预警数据和萤石云告警数据
    // 追加 SSE 消息到列表（用于页面渲染每条记录内容）
    const convertedSseMessages = sseMessages.map((msg, idx) => {
      const payload = msg?.data || {}
      // 兼容后端结构：{ code, message, data: {...} }
      const data = payload?.data || payload
      const messageTimeMs = Number(data?.messageTime || Date.now())
      const messageType = data?.messageType || 'sse.message'
      const rawBody = data?.messageBody

      let bodyObj = rawBody
      if (typeof rawBody === 'string') {
        try { bodyObj = JSON.parse(rawBody) } catch (e) {}
      }
      const identifier = bodyObj?.body?.identifier || bodyObj?.identifier

      return {
        id: msg?.id || `sse_msg_${idx}`,
        alert_level: 1,
        alert_type: `SSE: ${renderTextStr(messageType)}`,
        customer_name: renderTextStr(identifier) || 'SSE',
        device_name: renderTextStr(identifier) || 'SSE',
        device_serial: renderTextStr(identifier) || '',
        channel_no: 1,
        status: 0,
        create_time: Math.floor(messageTimeMs / 1000),
        updated_at: Math.floor(messageTimeMs / 1000),
        _sseMessage: true,
        _sseData: data,
        _sseBody: bodyObj,
      }
    })

    const merged = [
      ...monitoringServiceList,
      ...convertedYs7Alarms,
      ...convertedSseMessages
    ]
    // console.log('[ServiceAlerts] 监控合并列表 getMergedMonitoringServiceList', {
    //   alertsApi: monitoringServiceList,
    //   alertsApiCount: monitoringServiceList.length,
    //   ys7Alarms: convertedYs7Alarms,
    //   ys7Count: convertedYs7Alarms.length,
    //   sseConverted: convertedSseMessages,
    //   sseCount: convertedSseMessages.length,
    //   merged,
    //   mergedCount: merged.length
    // })
    return merged
  }

  componentDidUpdate(prevProps) {
    // 检查 Redux state 中的错误变化
    this.apiManager.checkReduxErrors(prevProps, () => {
      return {}
    })
    
    // 如果高度还未初始化，重新计算（压缩模式下 height 也为 null，不能用 height 判断）
    if (!this._heightsCalculated) {
      setTimeout(() => {
        this.calculateHeights()
      }, 50)
    }

    // SSE 调试：只在真正收到新消息时打印，并做节流（避免 render 刷屏）
    try {
      const prevStats = prevProps?.sseObj?.data?.stats || {}
      const curStats = this.props?.sseObj?.data?.stats || {}
      const prevTotal = Number(prevStats.totalReceived || 0)
      const curTotal = Number(curStats.totalReceived || 0)

      if (curTotal > prevTotal) {
        const now = Date.now()
        if (now - (this._lastSseLogAt || 0) > 1000) {
          this._lastSseLogAt = now
          this._lastSseTotalReceived = curTotal
          // 这里只打摘要，避免把大 payload 打进 console
          console.log('[SSE stats]', {
            connected: this.props?.sseObj?.data
          })
        }
      }
    } catch (e) {
      // ignore
    }
  }

  render() {
    const { storeObj, alertsObj, sseObj, app } = this.props
    const { ys7Token: ys7AccessToken = '' } = app || {}
    const { default_storeOption: storeOption } = storeObj
    const { data: { messages, connected: sseConnected = false } = {}, error: sseError = null } = sseObj
    /** 全量分桶：合并/兼容其它模块（如 getMergedMonitoringServiceList） */
    const sseMessages = getMonitoringSseMessageList(sseObj)
    /** 监控预警卡片：仅 HTTP 未处理 + SSE event `monitoring_alarm` */
    const monitoringAlarmSseForPanel = getMonitoringAlarmSseList(sseObj)
    const monitoringAlarmProcessedKeys = getMonitoringAlarmProcessedStreamKeysMap(sseObj)
    const { 
      nursing: { 
        list: nursingList = [], 
        query: nursingQuery = {}, 
        total: nursingTotal = 0, 
        pageNumber: nursingPageIndex = 1, 
        pageSize: nursingPageSize = 20 
      } = {},
      postpartumService: { 
        list: postpartumServiceList = [], 
        query: postpartumServiceQuery = {}, 
        total: postpartumServiceTotal = 0, 
        pageNumber: postpartumServicePageIndex = 1, 
        pageSize: postpartumServicePageSize = 20 
      } = {},
      monitoringService: {
        query: monitoringServiceQuery = {}
      } = {}
    } = alertsObj
    const selectedStoreId = getSelectedStoreId(nursingQuery)
    const monitoringGroupUnprocessedForPanel = filterMonitoringGroupUnprocessedByStoreId(
      this.props.monitoringGroupUnprocessed,
      selectedStoreId
    )
    const monitoringAlarmSseForSelectedStore = filterMonitoringSseMessagesByStoreId(
      monitoringAlarmSseForPanel,
      selectedStoreId
    )
    const headerAction = (
      <ServiceAlertsModal
        baseProps={{
        }}
        title="服务预警设置"
      >
        <Button>设置</Button>
      </ServiceAlertsModal>
    )
    return (
      <div>
        <PageHeader
          breadcrumbList={breadcrumbList}
          extra={headerAction}  
        />
        <div 
          className={`i2-container service-alerts-list ${this.state.isCompressed ? 'compressed-mode' : ''}`}
        >
          <Card className="i2-card-page">
            <section className="i2-app-toolbar">
              <div className="toolbar-filters">
                <SelectC
                  label=""
                  query={nursingQuery}
                  searchKey="store_id"
                  options={storeOption}
                  action={this.updateQuery}
                />
              </div>
            </section>
            <Row gutter={[6, 6]}>
              <Col {...COL_LAYOUT_CONFIG.monitoringCard}>
                <Card title="监控预警"
                  extra={
                    <Badge 
                      status={sseConnected ? 'success' : 'error'} 
                      text={sseConnected ? '已连接' : '正在连接'}
                      style={{ fontSize: 12 }}
                    />
                  }
                  className="alert-card"
                  style={{ height: !this.state.isCompressed && this.state.monitoringAlertCardHeight ? `${this.state.monitoringAlertCardHeight}px` : undefined }}
                >
                  <div className="alert-list-wrapper">
                    <MonitoringSseAlertsPanel
                      monitoringGroupUnprocessed={monitoringGroupUnprocessedForPanel}
                      monitoringAlarmSseMessages={monitoringAlarmSseForSelectedStore}
                      monitoringAlarmProcessedStreamKeys={monitoringAlarmProcessedKeys}
                      getStatusConfig={this.getStatusConfig}
                      monitoringServiceQuery={monitoringServiceQuery}
                      ys7AccessToken={ys7AccessToken}
                    />
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={24} lg={16}>
                <Row gutter={[6, 6]}>
                  <Col {...COL_LAYOUT_CONFIG.rightAlertCard}>
                    <Card title="护理预警"
                  className="alert-card"
                  style={{ height: !this.state.isCompressed && this.state.alertCardHeight ? `${this.state.alertCardHeight}px` : undefined }}
                >
                  <div className="alert-list-wrapper">
                    <List
                      dataSource={nursingList}
                    locale={{ emptyText: '暂无预警数据' }}
                    className="alert-list"
                    size="small"
                    renderItem={(item) => {
                      
                      const alertLevel = item.alert_level;
                      const alertConfig = this.getAlertConfig(alertLevel);
                      const statusConfig = this.getStatusConfig(item.status);
                      return (
                        <List.Item
                          className="alert-list-item"
                          style={{
                            borderLeft: alertConfig.borderLeft,
                            backgroundColor: alertConfig.backgroundColor,
                          }}
                        >
                          <div className="clickable-container">
                            <NursingAlertsDrawer
                              title={`${renderTextStr(item.customer_name)} 预警明细`}
                              baseProps={{
                                data: item,
                                query: nursingQuery,
                                serviceType: 'nursing',
                              }}
                            >
                              <div className="full-width">
                                <div className="tag-container">
                                  <div>
                                    <Tag
                                      color={alertConfig.color}
                                      style={alertConfig.tagStyle}
                                    >
                                      等级: {alertConfig.text}
                                    </Tag>
                                    <Tag color="blue">
                                      客户: {renderTextStr(item.customer_name)}
                                    </Tag>
                                    <Tag color={statusConfig.color} style={statusConfig.tagStyle}>
                                      {statusConfig.text}
                                    </Tag>
                                  </div>
                                  <div className="type-container">
                                    <span>
                                      类型:{' '}
                                      <span className="type-value">
                                        {renderTextStr(item.alert_type)}
                                      </span>
                                    </span>
                                  </div>
                                  <span className="time-text">
                                    时间: {formatDateTimeStr(item.updated_at)}
                                  </span>
                                </div>
                              </div>
                            </NursingAlertsDrawer>
                          </div>
                        </List.Item>
                      );
                    }}
                    />
                    <div className="pagination-container">
                      <Pagination
                        size="small"
                        current={nursingPageIndex}
                        pageSize={nursingPageSize}
                        total={nursingTotal}
                        showSizeChanger
                        showTotal={(t) =>
                          `第 ${nursingPageIndex} 页，每页 ${nursingPageSize} 条，共 ${t} 条`
                        }
                        onChange={(page, size) =>
                          this.handlePageChange(page, size, nursingQuery)
                        }
                        onShowSizeChange={(page, size) =>
                          this.handlePageChange(1, size, nursingQuery)
                        }
                      />
                    </div>
                  </div>
                </Card>
                  </Col>
                  <Col {...COL_LAYOUT_CONFIG.rightAlertCard}>
                    <Card title="产康预警"
                  className="alert-card"
                  style={{ height: !this.state.isCompressed && this.state.alertCardHeight ? `${this.state.alertCardHeight}px` : undefined }}
                >
                  <div className="alert-list-wrapper">
                    <List
                      dataSource={postpartumServiceList}
                    locale={{ emptyText: '暂无预警数据' }}
                    className="alert-list"
                    size="small"
                    renderItem={(item) => {
                      
                      const alertLevel = item.alert_level;
                      const alertConfig = this.getAlertConfig(alertLevel);
                      const statusConfig = this.getStatusConfig(item.status);
                      return (
                        <List.Item
                          className="alert-list-item"
                          style={{
                            borderLeft: alertConfig.borderLeft,
                            backgroundColor: alertConfig.backgroundColor,
                          }}
                        >
                          <div className="clickable-container">
                            <NursingAlertsDrawer
                              title={`${renderTextStr(item.customer_name)} 预警明细`}
                              baseProps={{
                                data: item,
                                query: nursingQuery,
                                serviceType: 'nursing',
                              }}
                            >
                              <div className="full-width">
                                <div className="tag-container">
                                  <div>
                                    <Tag
                                      color={alertConfig.color}
                                      style={alertConfig.tagStyle}
                                    >
                                      等级: {alertConfig.text}
                                    </Tag>
                                    <Tag color="blue">
                                      客户: {renderTextStr(item.customer_name)}
                                    </Tag>
                                    <Tag color={statusConfig.color} style={statusConfig.tagStyle}>
                                      {statusConfig.text}
                                    </Tag>
                                  </div>
                                  <div className="type-container">
                                    <span>
                                      类型:{' '}
                                      <span className="type-value">
                                        {renderTextStr(item.alert_type)}
                                      </span>
                                    </span>
                                  </div>
                                  <span className="time-text">
                                    时间: {formatDateTimeStr(item.updated_at)}
                                  </span>
                                </div>
                              </div>
                            </NursingAlertsDrawer>
                          </div>
                        </List.Item>
                      );
                    }}
                    />
                    <div className="pagination-container">
                      <Pagination
                        size="small"
                        current={nursingPageIndex}
                        pageSize={nursingPageSize}
                        total={nursingTotal}
                        showSizeChanger
                        showTotal={(t) =>
                          `第 ${nursingPageIndex} 页，每页 ${nursingPageSize} 条，共 ${t} 条`
                        }
                        onChange={(page, size) =>
                          this.handlePageChange(page, size, nursingQuery)
                        }
                        onShowSizeChange={(page, size) =>
                          this.handlePageChange(1, size, nursingQuery)
                        }
                      />
                    </div>
                  </div>
                </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row gutter={[6, 6]} style={{ marginTop: 6 }}>
              <Col span={24}>
                <Card 
                  title="视频实时监控"
                  className="video-monitoring-card"
                  style={{ height: !this.state.isCompressed && this.state.videoCardHeight ? `${this.state.videoCardHeight}px` : undefined }}
                >
                  <div className="video-monitoring-grid">
                    {/* 视频监控画面 - 显示前4个监控设备 */}
                    {this.getMonitoringDevices(this.getMergedMonitoringServiceList()).slice(0, 4).map((device, index) => {
                      const containerId = `ys7-video-${device.deviceSerial || index}`
                      const aspectRatio = 16 / 9
                      const isMonitoringPlaying = Boolean(
                        this.state.monitoringPlayingByContainer[containerId]
                      )

                      return (
                        <div 
                          key={device.deviceSerial || index}
                          className={`video-monitoring-item ${device.status === 1 ? 'status-online' : 'status-offline'}`}
                          style={{
                            aspectRatio
                          }}
                          onClick={() => {
                            // 点击查看单个监控画面详情
                            this.handleViewMonitoringDetail(device);
                          }}
                        >
                          <EzvizPlayer
                            clickToPlay
                            wrapperStyle={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0
                            }}
                            clickHint={
                              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
                                点击播放
                              </span>
                            }
                            containerId={containerId}
                            containerClassName="ys7-video-player-container"
                            containerStyle={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              height: '100%'
                            }}
                            active={Boolean(ys7AccessToken && device.deviceSerial)}
                            accessToken={ys7AccessToken}
                            deviceSerial={device.deviceSerial}
                            channelNo={device.channelNo || 1}
                            mode="live"
                            rotateDeg={180}
                            initDelayMs={index * 300}
                            resetStreamSignal={this.state.monitoringStreamReset[containerId] ?? 0}
                            onClickToPlayChange={(on) => {
                              this.setState((prev) => ({
                                monitoringPlayingByContainer: {
                                  ...prev.monitoringPlayingByContainer,
                                  [containerId]: on
                                }
                              }))
                            }}
                            playerProps={{ width: '100%', height: '100%' }}
                            onReady={(player) => {
                              this.videoPlayers[containerId] = player
                            }}
                            onDestroy={() => {
                              delete this.videoPlayers[containerId]
                            }}
                            onError={(err) => {
                              console.error('实时视频播放错误:', err)
                            }}
                          />
                          {isMonitoringPlaying ? (
                            <Button
                              type="primary"
                              danger
                              size="small"
                              className="video-monitoring-stop-live"
                              onClick={(e) => {
                                e.stopPropagation()
                                this.stopMonitoringStream(containerId)
                              }}
                            >
                              停止
                            </Button>
                          ) : null}
                          {/* 设备信息覆盖层 */}
                          <div className="video-monitoring-content">
                            <div className="video-monitoring-info">
                              <div className="video-monitoring-name">
                                {device.deviceName || `监控设备 ${index + 1}`}
                              </div>
                              <div className={`video-monitoring-status ${device.status === 1 ? 'status-online' : 'status-offline'}`}>
                                {device.status === 1 ? '在线' : '离线'}
                              </div>
                              <div className="video-monitoring-serial">
                                {device.deviceSerial || `设备序列号 ${index + 1}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  {this.getMonitoringDevices(this.getMergedMonitoringServiceList()).length === 0 && (
                    <div className="empty-state">
                      暂无监控画面
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          </Card>
        </div>
      </div>
    );
  }
}

const ServiceAlertsListWrapper = () => {
  const dispatch = useDispatch()
  const monitoringGroupUnprocessed = useSelector(
    (state) => state.alerts.monitoringGroupUnprocessed
  )
  const props = {
    dispatch,
    app: useSelector((state) => state.app),
    storeObj: useSelector((state) => state.dashboard.storeList),
    alertsObj: useSelector((state) => state.alerts.alerts),
    ys7DeviceObj: useSelector((state) => state.ys7.device),
    ys7AlarmObj: useSelector((state) => state.ys7.alarm),
    sseObj: useSelector((state) => state.sse),
    monitoringGroupUnprocessed
  }
  return <ServiceAlertsList {...props} />
}

export default ServiceAlertsListWrapper;
